/**
 * Get collaboration status chip configuration
 * Returns background color, text color, and label for displaying collaboration status
 */
export const getCollaborationStatusConfig = (
  status: string,
  isRequester?: boolean,
  requesterPaid?: boolean,
  receiverPaid?: boolean
): { bg: string; text: string; label: string } => {
  // Base configurations for static statuses
  const configs: Record<string, { bg: string; text: string; label: string }> = {
    REQUESTED: {
      bg: "var(--mantine-color-primary-1)",
      text: "var(--mantine-color-primary-7)",
      label: "Request Sent",
    },
    RECEIVER_DECLINED: {
      bg: "var(--mantine-color-red-0)",
      text: "var(--mantine-color-red-9)",
      label: "Request Declined",
    },
    BOTH_ACCEPTED: {
      bg: "var(--mantine-color-primary-0)",
      text: "var(--mantine-color-primary-7)",
      label: "Request Accepted",
    },
    REQUESTER_PAID_ONLY: {
      bg: "var(--mantine-color-red-1)",
      text: "var(--mantine-color-red-7)",
      label: "Collaborator Payment Pending",
    },
    RECEIVER_PAID_ONLY: {
      bg: "var(--mantine-color-green-0)",
      text: "var(--mantine-color-green-7)",
      label: "Collaborator Payment Done",
    },
    PAYMENT_EXPIRED: {
      bg: "var(--mantine-color-orange-1)",
      text: "var(--mantine-color-orange-7)",
      label: "Payment Window Expired",
    },
    BOTH_PAID: {
      bg: "var(--mantine-color-green-0)",
      text: "var(--mantine-color-green-7)",
      label: "Payment Done",
    },
  };

  // Handle dynamic statuses based on payment state

  if (
    isRequester !== undefined &&
    requesterPaid !== undefined &&
    receiverPaid !== undefined &&
    (status === "REQUESTER_PAID_ONLY" || status === "RECEIVER_PAID_ONLY" || status === "BOTH_ACCEPTED")
  ) {
    const hasPaid = isRequester ? requesterPaid : receiverPaid;
    const otherHasPaid = isRequester ? receiverPaid : requesterPaid;

    // User has paid, waiting for collaborator
    if (hasPaid && !otherHasPaid) {
      return {
        bg: "var(--mantine-color-red-0)",
        text: "var(--mantine-color-red-7)",
        label: "Collaborator Payment Pending",
      };
    }

    // Collaborator has paid, user needs to pay
    if (!hasPaid && otherHasPaid) {
      return {
        bg: "var(--mantine-color-green-0)",
        text: "var(--mantine-color-green-7)",
        label: "Collaborator Payment Done",
      };
    }

    // Both haven't paid yet (only for BOTH_ACCEPTED status)
    if (!hasPaid && !otherHasPaid && status === "BOTH_ACCEPTED") {
      return {
        bg: "var(--mantine-color-red-0)",
        text: "var(--mantine-color-red-7)",
        label: "Collaborator Payment Pending",
      };
    }
  }

  // Return static config or fallback
  return (
    configs[status] || {
      bg: "var(--mantine-color-gray-0)",
      text: "var(--mantine-color-gray-7)",
      label: status,
    }
  );
};
